"use client";

import {
    ArrowRight,
    Heart,
    Loader2,
    Plus,
} from "lucide-react";
import { FlowStepNavigation } from "@/components/design/registration/FlowStepNavigation";
import { RegistrationInfoModal } from "@/components/design/registration/RegistrationInfoModal";
import { AppButton } from "@/components/design/forms";
import { useTranslations } from "next-intl";
import { ParticipantCard } from "./ParticipantCard";
import { RegistrationFlowHero } from "./RegistrationFlowHero";
import { RegistrationFlowRegisteredBanner } from "./RegistrationFlowRegisteredBanner";
import { AutoSaveIndicator } from "@/components/design/registration/AutoSaveIndicator";
import { RegistrationFlowAutoSaveNote } from "./RegistrationFlowAutoSaveNote";
import { useRegistrationFlowPage } from "./useRegistrationFlowPage";
import { isRegistrationFlowPrimaryParticipantRow } from "./registrationFlowPrimaryParticipant";

export default function RegistrationFlowPage() {
    const t = useTranslations("strings.registrationFlow");
    const {
        registeredParticipants,
        participants,
        primaryFormIndex,
        errors,
        showInfoModal,
        setShowInfoModal,
        updateParticipant,
        toggleParticipantExpansion,
        isParticipantValid,
        handleAddParticipant,
        handleRemoveParticipant,
        participantRowCanRemove,
        handleSubmit,
        isSubmitting,
        allParticipantsValid,
        setPrimaryFormIndex,
        registrationCabinetUserId,
        cabinetOwnerParticipantId,
        showGuestAutosaveBanner,
        guestAutoSaveLastAt,
    } = useRegistrationFlowPage();

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden bg-gradient-to-br from-fest-canvas via-fest-white to-fest-canvas">
            <div className="container mx-auto min-w-0 max-w-5xl flex-1 px-3 py-8 sm:px-4 md:py-12">
                <FlowStepNavigation />

                {showGuestAutosaveBanner && <AutoSaveIndicator />}

                {registeredParticipants.length > 0 && (
                    <RegistrationFlowRegisteredBanner
                        count={registeredParticipants.length}
                    />
                )}

                <RegistrationFlowHero
                    participantCount={registeredParticipants.length}
                    onOpenInfo={() => setShowInfoModal(true)}
                />

                <div className="mb-6 space-y-4">
                    {participants.map((participant, index) => {
                        const primaryProfileMode = registrationCabinetUserId
                            ? participant.id === registrationCabinetUserId
                                ? ("cabinetOwnerBadge" as const)
                                : ("off" as const)
                            : participants.length === 1
                              ? ("lockedOne" as const)
                              : ("choose" as const);

                        const isPrimaryProfile =
                            isRegistrationFlowPrimaryParticipantRow(
                                participant,
                                index,
                                participants,
                                primaryFormIndex,
                                {
                                    sessionParticipantId:
                                        registrationCabinetUserId,
                                    cabinetOwnerParticipantId,
                                },
                            );

                        return (
                            <ParticipantCard
                                key={index}
                                participant={participant}
                                index={index}
                                isValid={isParticipantValid(index)}
                                errors={errors[index] || {}}
                                onUpdate={(data) =>
                                    updateParticipant(index, data)
                                }
                                onToggle={() =>
                                    toggleParticipantExpansion(index)
                                }
                                onRemove={() => {
                                    void handleRemoveParticipant(index);
                                }}
                                canRemove={participantRowCanRemove(index)}
                                isPrimaryProfile={isPrimaryProfile}
                                primaryProfileLocked={participants.length === 1}
                                onSetPrimary={() => setPrimaryFormIndex(index)}
                                primaryProfileMode={primaryProfileMode}
                                phoneOptional={!isPrimaryProfile}
                            />
                        );
                    })}
                </div>

                <AppButton
                    onClick={handleAddParticipant}
                    className="mb-8 flex w-full min-w-0 max-w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-fest-gold bg-white px-4 py-5 text-base font-bold text-fest-heading shadow-sm transition-all hover:bg-fest-gold/5 sm:px-8 sm:py-6 sm:text-lg"
                >
                    <Plus className="h-6 w-6 text-fest-gold" />
                    <span>{t("addParticipant")}</span>
                </AppButton>

                <AppButton
                    onClick={() => void handleSubmit()}
                    disabled={!allParticipantsValid || isSubmitting}
                    className={`flex w-full min-w-0 max-w-full items-center justify-center gap-2 rounded-2xl px-4 py-5 text-base font-bold shadow-lg transition-all sm:gap-3 sm:px-10 sm:py-6 sm:text-xl ${
                        allParticipantsValid && !isSubmitting
                            ? "bg-gradient-to-r from-fest-gold to-fest-gold-mid text-white hover:scale-[1.02] hover:shadow-2xl hover:shadow-fest-gold/40"
                            : "cursor-not-allowed bg-fest-border-warm text-fest-subtle-text"
                    }`}
                >
                    {isSubmitting ? (
                        <Loader2
                            className="h-6 w-6 shrink-0 animate-spin"
                            aria-hidden
                        />
                    ) : (
                        <Heart className="h-6 w-6 fill-current" />
                    )}
                    <span>
                        {isSubmitting
                            ? t("submitSaving")
                            : registeredParticipants.length > 0
                              ? t("saveChanges")
                              : t("completeRegistration")}
                    </span>
                    {!isSubmitting ? (
                        <ArrowRight className="h-6 w-6" aria-hidden />
                    ) : null}
                </AppButton>

                {!allParticipantsValid && (
                    <p className="mt-4 text-center text-sm text-fest-subtle-text">
                        {t("fillRequiredHint")}
                    </p>
                )}

                {showGuestAutosaveBanner && (
                    <RegistrationFlowAutoSaveNote
                        lastSaved={guestAutoSaveLastAt}
                    />
                )}
            </div>

            {showInfoModal && (
                <RegistrationInfoModal
                    onClose={() => setShowInfoModal(false)}
                />
            )}
        </div>
    );
}
